/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { service } from "@ember/service";
import dayjs from "dayjs";
import type { EmberChangeset } from "ember-changeset";
import {
  validateNumber,
  validatePresence
} from "ember-changeset-validations/validators";
import type { IntlService } from "ember-intl";
import type { ForcBaseArgs } from "parog-web/components/forc/abstract/base-component";
import JournalEventBasedComponent from "parog-web/components/forc/abstract/journal-event-based-component";
import JournalEventRetrievalStrategies from "parog-web/components/forc/utils/journal-event-retrieval-strategies";
import ThymicEpisodeOccurredJournalEvent from "parog-web/models/thymic-episode-occurred-journal-event";

import styles from "./index.module.css";

interface Args extends ForcBaseArgs {
  onChangesetCreated: (changeset: EmberChangeset) => void;
  onChangesetDestroyed: (changeset: EmberChangeset) => void;
  forceQuit: boolean;
  model: Record<string, unknown>;
}

/* eslint-disable parog-web/migrate-non-patch-forms */
export default class ThymicEpisodeForm extends JournalEventBasedComponent<Args> {
  @service declare intl: IntlService;

  styles = styles;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    if (this.args.onChangesetCreated) {
      this.args.onChangesetCreated(this.changeset);
    }
    this.forceQuit = this.args.forceQuit || false;
  }

  static journalEventClass = ThymicEpisodeOccurredJournalEvent;
  journalEventProperties = [
    "occurredAt",
    "episodeType",
    "episodeSeverity",
    "withAnxiousDistress",
    "withMixedCharacteristics",
    "withPsychoticCongruentWithMood",
    "withPsychoticNotCongruentWithMood",
    "withCatatonia",
    "withMelancholicCharacteristics",
    "wihOnsetDuringPeripartum",
    "withAtypicalCharacteristics",
    "episodeDuration",
    "hospitalization",
    "tsPresence",
    "commentary"
  ];

  retrieveJournalEvent = JournalEventRetrievalStrategies.sourceJournalEvent;

  willDestroy() {
    if (this.args.onChangesetDestroyed) {
      this.args.onChangesetDestroyed(this.changeset);
    }
  }

  static forcDisplayName = "Épisode thymique";
  static fullComponentId = "thymic-episode/edition-form";

  get validations() {
    // FIXME add the required validatiosn in the yml + change the type of episodeDuration
    const validations = this.journalEventClass.validations(this.intl);

    validations["hospitalization"] = validatePresence({
      presence: true,
      description: "L'hospitalisation"
    });
    validations["tsPresence"] = validatePresence({
      presence: true,
      description: "La présence d'une TS"
    });
    validations["episodeDuration"] = validateNumber({
      integer: true,
      positive: true,
      allowBlank: true,
      description: "La durée de l'épisode en semaine"
    });
    return validations;
  }

  get isDepressiveEpisode() {
    return this.changeset.get("episodeType") === "depressive";
  }

  get patientAgeAtTheTimeOfTheEpisode() {
    const episodeDate = this.changeset.get("occurredAt");
    const birthday = this.args.patient.birthday;

    return episodeDate
      ? dayjs(episodeDate).diff(dayjs(birthday), "years")
      : null;
  }

  retrieveModel() {
    return this.args.model || super.retrieveModel();
  }
}
